// src/pages/Admin/ApiGatewayTest.js
import React, { useState } from 'react';
import axios from 'axios';
import TitleWithBack from '../../components/common/TitleWithBack';

function ApiGatewayTest() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');

  const [stageLoading, setStageLoading] = useState(null);
  const [stageMessage, setStageMessage] = useState('');
  const [stageError, setStageError] = useState('');

  const runTest = () => {
    setLoading(true);
    setError('');
    setResult(null);
    axios.get('/api/api-gateway/test/program-categories')
      .then((response) => {
        setResult(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message || 'Could not reach the API Gateway.');
        setLoading(false);
      });
  };

  const runSyncNow = () => {
    setSyncing(true);
    setSyncMessage('');
    setSyncError('');
    axios.post('/api/api-gateway/sync/run')
      .then((response) => {
        setSyncMessage(response.data);
        setSyncing(false);
      })
      .catch((err) => {
        setSyncError(err.response?.data || err.message || 'Sync failed.');
        setSyncing(false);
      });
  };

  const runStage = (stage, path) => {
    setStageLoading(stage);
    setStageMessage('');
    setStageError('');
    axios.post(`/api/api-gateway/sync/${path}`)
      .then((response) => {
        setStageMessage(`${stage}: ${response.data}`);
        setStageLoading(null);
      })
      .catch((err) => {
        setStageError(`${stage}: ${err.response?.data || err.message || 'failed.'}`);
        setStageLoading(null);
      });
  };

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="API Gateway Connection Test" />
      <p>
        Calls the program-categories endpoint through the configured API Gateway
        credentials to confirm the connection works.
      </p>
      <button className="btn btn-primary mb-3" onClick={runTest} disabled={loading}>
        {loading ? 'Testing...' : 'Test Connection'}
      </button>

      {error && <div className="alert alert-danger">{error}</div>}

      {result && (
        <>
          <div className="alert alert-success">
            Connected. Received {result.length} program categor{result.length === 1 ? 'y' : 'ies'}.
          </div>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Gateway ID</th>
                <th>Name</th>
                <th>Code</th>
              </tr>
            </thead>
            <tbody>
              {result.map((cat) => (
                <tr key={cat.eimsId}>
                  <td>{cat.eimsId}</td>
                  <td>{cat.name}</td>
                  <td>{cat.code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <hr className="my-4" />

      <h5>Fetch Individually</h5>
      <p>
        Reference data only - cheap, independent, no series/programs walk.
        Runs automatically at midnight either way.
      </p>
      <div className="d-flex gap-2 mb-3">
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Categories', 'categories')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Categories' ? 'Fetching...' : 'Fetch Categories'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Levels', 'levels')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Levels' ? 'Fetching...' : 'Fetch Levels'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Series', 'series')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Series' ? 'Fetching...' : 'Fetch Series'}
        </button>
      </div>
      {stageMessage && <div className="alert alert-success">{stageMessage}</div>}
      {stageError && <div className="alert alert-danger">{stageError}</div>}

      <hr className="my-4" />

      <h5>Fetch by Stage</h5>
      <p>
        Programs, Semesters, Modules and Papers still have to walk through
        every stage before them (a program's papers can't be fetched without
        first fetching the program and its structure), and each stops at the
        named stage instead of continuing all the way to candidates. Only
        applies to series with API Gateway Fetch enabled and at least one
        category/level combination selected. These were timing out on the
        full walk, so Fetch Modules now only fetches modules for up to 5
        programs per click, skipping any program it already fetched modules
        for in the last 10 minutes. Fetch Centres and Fetch Candidates read
        the paper list already stored locally instead of re-walking the
        gateway, and only fetch up to 20 papers per click, skipping any
        paper already fetched in the last 10 minutes. Click these
        repeatedly (or wait for the nightly sync) to work through the rest.
      </p>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Programs', 'programs')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Programs' ? 'Fetching...' : 'Fetch Programs'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Semesters', 'semesters')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Semesters' ? 'Fetching...' : 'Fetch Semesters'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Modules', 'modules')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Modules' ? 'Fetching...' : 'Fetch Modules'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Papers', 'papers')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Papers' ? 'Fetching...' : 'Fetch Papers'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Centres', 'centres')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Centres' ? 'Fetching...' : 'Fetch Centres'}
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => runStage('Candidates', 'candidates')}
          disabled={stageLoading !== null}
        >
          {stageLoading === 'Candidates' ? 'Fetching...' : 'Fetch Candidates'}
        </button>
      </div>

      <hr className="my-4" />

      <h5>Run Full Sync</h5>
      <p>
        Pulls categories, levels, series, and (for series with API Gateway
        Fetch enabled and at least one category/level combination selected)
        their programs, papers, centres, and candidates. This is the slow,
        full walk - the gateway has been taking minutes per program, so
        expect this to take a while. Runs automatically every night at
        midnight either way; use this to run it on demand instead of waiting.
      </p>
      <button className="btn btn-primary mb-3" onClick={runSyncNow} disabled={syncing}>
        {syncing ? 'Syncing...' : 'Run Full Sync Now'}
      </button>
      {syncMessage && <div className="alert alert-success">{syncMessage}</div>}
      {syncError && <div className="alert alert-danger">{syncError}</div>}
    </div>
  );
}

export default ApiGatewayTest;
